import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface CalendarDay {
  day: number;
  hours: string;
  isDisabled: boolean;
  isToday: boolean;
  isSelected: boolean;
}

export interface AttendanceData {
  date: string;
  hours: string;
  isPresent: boolean;
}

/**
 * AttendanceCalendarComponent renders a month-view attendance calendar, maps attendance records to calendar days,
 * supports date selection, month navigation, and gesture-based navigation (touch swipes and mouse wheel).
 * It generates a fixed 6-week grid, disables future dates, and emits events when the selected date or visible month changes.
 * @component
 * @selector app-attendance-calendar
 * @example
 *   <app-attendance-calendar
 *     [attendanceData]="attendanceArray"
 *     [currentMonth]="currentMonthDate"
 *     [selectedDate]="selectedDate"
 *     (dateSelected)="onDateSelected($event)"
 *     (monthChanged)="onMonthChanged($event)">
 *   </app-attendance-calendar>
 * @input {AttendanceData[]} attendanceData - Array of attendance records (expects date strings in ISO format and hours), used to populate day hours in the calendar.
 * @input {Date} currentMonth - The month to display (any Date within the month is accepted); component derives month/year from this value.
 * @input {(Date|null)} selectedDate - The currently selected date (null if none); used to highlight a day in the grid.
 * @output {EventEmitter<Date>} dateSelected - Emitted when a user selects or navigates to a date; payload is the selected Date.
 * @output {EventEmitter<Date>} monthChanged - Emitted when the visible month changes (via navigation or date navigation that crosses month boundaries); payload is the new month's Date (first day of month).
 *
 * Key methods:
 *  - updateCalendar(): Recomputes month name/year and regenerates calendarDays for the currentMonth and attendanceData.
 *  - generateCalendarDays(): Builds a 6-week grid (42 cells), fills previous/next month filler days, marks today, selected day, disabled (future) days, and applies attendance hours using parseDateWithoutTimezone().
 *  - parseDateWithoutTimezone(dateString: string): Parses an ISO date string by extracting YYYY-MM-DD and creating a local Date to avoid UTC shift issues.
 *  - navigateMonth(direction: 'prev'|'next'): Shifts currentMonth to previous/next month, updates calendar and emits monthChanged.
 *  - navigateDate(direction: 'prev'|'next'): Moves selection to previous/next date (prevents future selection), updates month when crossing boundaries, and emits dateSelected.
 *  - selectDate(day: CalendarDay): Selects a given day from the grid (ignores disabled days) and emits dateSelected.
 *  - getMonthYearDisplay(): Returns a human-friendly "Mon YYYY" string used by the UI.
 *  - handleSwipeGesture(), processWheelGestureAfterDelay(): Internal helpers implementing touch swipe and wheel gesture logic to navigate dates.
 *  - setupGlobalWheelListener(), globalWheelHandler: Manage a global wheel listener to ensure wheel gestures are finalized when scrolling leaves the calendar container.
 *
 * Important lifecycle hooks:
 *  - ngOnInit(): Initializes the calendar (updateCalendar) and registers the global wheel listener.
 *  - ngOnChanges(changes: SimpleChanges): Recomputes calendar when attendanceData, currentMonth, or selectedDate inputs change.
 *  - ngOnDestroy(): Cleans up timeouts and removes the global wheel listener.
 *
 * Notes:
 *  - The component exposes a ViewChild calendarContainer for wheel event containment and prevents default vertical scrolling while processing horizontal gestures.
 *  - Touch and wheel gesture behavior is tuned with thresholds (scrollThreshold, maxVerticalDistance, wheelThreshold, wheelTimeoutDelay) to avoid accidental navigation.
 */
@Component({
  selector: 'app-attendance-calendar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './attendance-calendar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./attendance-calendar.component.scss'],
})
export class AttendanceCalendarComponent implements OnInit, OnDestroy, OnChanges {
  @Input() attendanceData: AttendanceData[] = [];
  @Input() currentMonth: Date = new Date();
  @Input() selectedDate: Date | null = null;
  @Input() today: Date = new Date();
  @Output() dateSelected = new EventEmitter<Date>();
  @Output() monthChanged = new EventEmitter<Date>();

  @ViewChild('calendarContainer', { static: false }) calendarContainer!: ElementRef;

  calendarDays: CalendarDay[] = [];
  currentMonthName = '';
  currentYear = 0;

  weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  // Touch/swipe handling properties
  private touchStartX = 0;
  private touchStartY = 0;
  private touchEndX = 0;
  private touchEndY = 0;
  private isScrolling = false;
  private scrollThreshold = 50; // Minimum distance for swipe
  private maxVerticalDistance = 100; // Max vertical distance to consider it horizontal swipe

  // Gesture-based navigation properties
  private gestureDirection: 'left' | 'right' | null = null;
  private gestureStartTime = 0;
  private gestureProcessed = false;
  private wheelAccumulator = 0;
  private wheelThreshold = 15; // Lower threshold for better sensitivity
  private wheelTimeout: any = null;
  private wheelTimeoutDelay = 80; // Much shorter delay for immediate response
  private isWheelGestureActive = false;
  private lastWheelEventTime = 0;

  ngOnInit(): void {
    this.updateCalendar();
    this.setupGlobalWheelListener();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['attendanceData'] ||
      changes['currentMonth'] ||
      changes['selectedDate'] ||
      changes['today']
    ) {
      this.updateCalendar();
    }
  }

  ngOnDestroy(): void {
    // Cleanup timeouts
    if (this.wheelTimeout) {
      clearTimeout(this.wheelTimeout);
    }
    // Remove global wheel listener
    document.removeEventListener('wheel', this.globalWheelHandler);
  }

  updateCalendar(): void {
    this.currentMonthName = this.currentMonth.toLocaleDateString('en-US', { month: 'short' });
    this.currentYear = this.currentMonth.getFullYear();
    this.generateCalendarDays();
  }

  /**
   * Populate this.calendarDays with 42 day objects for the component's currentMonth, including previous/next month padding, attendance hours, and flags for isToday, isSelected, and isDisabled.
   * @example
   * generateCalendarDays()
   * void
   * @param {void} none - Function takes no arguments.
   * @returns {void} Populates this.calendarDays; does not return a value.
   **/
  generateCalendarDays(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    // Get first day of month and number of days
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    // Get previous month's days to fill the grid
    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();

    this.calendarDays = [];

    // Add previous month's trailing days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      this.calendarDays.push({
        day,
        hours: '',
        isDisabled: true,
        isToday: false,
        isSelected: false,
      });
    }

    // Add current month's days
    const today = this.getTodayDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      const isToday = this.isSameDay(currentDate, today);
      const isSelected = this.selectedDate ? this.isSameDay(currentDate, this.selectedDate) : false;
      const isFutureDate = currentDate > today; // Disable dates after today

      // Find attendance data for this day
      // Parse date string without timezone conversion (extract YYYY-MM-DD from ISO string)
      const attendance = this.attendanceData.find(data => {
        const dataDate = this.parseDateWithoutTimezone(data.date);
        return this.isSameDay(dataDate, currentDate);
      });

      this.calendarDays.push({
        day,
        hours: attendance?.hours || '00:00',
        isDisabled: isFutureDate, // Disable future dates
        isToday,
        isSelected,
      });
    }

    // Add next month's leading days to complete the grid
    const remainingDays = 42 - this.calendarDays.length; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      this.calendarDays.push({
        day,
        hours: '',
        isDisabled: true,
        isToday: false,
        isSelected: false,
      });
    }
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  /**
   * Parse date string without timezone conversion
   * Extracts YYYY-MM-DD from ISO string and creates a Date object in local timezone
   * This prevents UTC dates like "2026-01-27T00:00:00.000Z" from being converted to previous day
   */
  private parseDateWithoutTimezone(dateString: string): Date {
    // Extract date part (YYYY-MM-DD) from ISO string
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    // Create date in local timezone using the date components directly
    return new Date(year, month - 1, day);
  }

  /**
   * Navigate the calendar to the previous or next month.
   * @example
   * navigateMonth('prev')
   * undefined
   * @param {'prev' | 'next'} direction - Direction to navigate the calendar; 'prev' moves to the previous month, 'next' moves to the next month.
   * @returns {void} Updates the component's currentMonth, refreshes the calendar view, and emits a monthChanged event.
   **/
  navigateMonth(direction: 'prev' | 'next'): void {
    const newMonth = new Date(this.currentMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }

    this.currentMonth = newMonth;
    this.updateCalendar();
    this.monthChanged.emit(this.currentMonth);
  }

  /**
   * Navigate to previous or next date
   */
  navigateDate(direction: 'prev' | 'next'): void {
    const today = this.getTodayDate();

    // Use selected date or current month's first day as starting point
    const currentDate = this.selectedDate
      ? new Date(this.selectedDate)
      : new Date(this.currentMonth);
    if (!this.selectedDate) {
      currentDate.setDate(1);
    }
    currentDate.setHours(0, 0, 0, 0);

    // Calculate new date
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }

    // Don't allow navigation to future dates
    if (newDate > today) {
      return;
    }

    // Check if month changed
    const monthChanged =
      newDate.getMonth() !== this.currentMonth.getMonth() ||
      newDate.getFullYear() !== this.currentMonth.getFullYear();

    if (monthChanged) {
      // Update current month
      this.currentMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
      this.updateCalendar();
      this.monthChanged.emit(this.currentMonth);
    }

    // Select the new date
    this.selectedDate = newDate;

    // Update selection state in calendar
    this.calendarDays.forEach(day => {
      const dayDate = new Date(
        this.currentMonth.getFullYear(),
        this.currentMonth.getMonth(),
        day.day
      );
      day.isSelected = this.isSameDay(dayDate, newDate) && !day.isDisabled;
    });

    // Emit date selection
    this.dateSelected.emit(newDate);
  }

  private getTodayDate(): Date {
    const today = new Date(this.today);
    today.setHours(0, 0, 0, 0);
    return today;
  }

  /**
   * Selects the given calendar day, updates selection state, and emits the selected Date.
   * @example
   * electDate({ day: 15, isDisabled: false })
   * undefined
   * @param {{CalendarDay}} {{day}} - The calendar day object to select; selection is ignored if the day is disabled.
   * @returns {{void}} Does not return a value; updates component state and emits the selected date via dateSelected.
   **/
  selectDate(day: CalendarDay): void {
    if (day.isDisabled) return;

    const selectedDate = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth(),
      day.day
    );
    this.selectedDate = selectedDate;

    // Update selection state
    this.calendarDays.forEach(d => (d.isSelected = false));
    day.isSelected = true;

    this.dateSelected.emit(selectedDate);
  }

  getMonthYearDisplay(): string {
    return `${this.currentMonthName} ${this.currentYear}`;
  }

  onDayClick(day: CalendarDay): void {
    this.selectDate(day);
  }

  // Touch event handlers for mobile swipe gestures
  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 1) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.isScrolling = false;
      this.gestureDirection = null;
      this.gestureProcessed = false;
      this.gestureStartTime = Date.now();
    }
  }

  /**
   * Handle touchmove events to detect horizontal swipe gestures and update scrolling state and gesture direction.
   * @example
   * onTouchMove(touchEvent)
   * undefined
   * @param {{TouchEvent}} {{event}} - The TouchEvent from the touchmove listener.
   * @returns {{void}} No return value; updates component state (isScrolling, gestureDirection) and may call preventDefault to stop native scrolling.
   **/
  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (event.touches.length === 1 && !this.gestureProcessed) {
      const currentX = event.touches[0].clientX;
      const currentY = event.touches[0].clientY;
      const deltaX = currentX - this.touchStartX;
      const deltaY = Math.abs(currentY - this.touchStartY);

      // Determine if this is a horizontal swipe
      if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 10) {
        this.isScrolling = true;
        event.preventDefault(); // Prevent default scrolling behavior

        // Determine gesture direction
        if (deltaX > 0) {
          this.gestureDirection = 'right';
        } else {
          this.gestureDirection = 'left';
        }
      }
    }
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (event.changedTouches.length === 1 && this.isScrolling && !this.gestureProcessed) {
      this.touchEndX = event.changedTouches[0].clientX;
      this.touchEndY = event.changedTouches[0].clientY;
      this.handleSwipeGesture();
    }
    this.isScrolling = false;
  }

  // Mouse wheel event handler for desktop horizontal scrolling
  /**
   * Handle wheel events on the calendar container to implement horizontal scrolling and schedule gesture processing.
   * @example
   * onWheel(event)
   * void
   * @param {{WheelEvent}} {{event}} - DOM wheel event; vertical scrolling is prevented and horizontal delta is used to navigate the calendar.
   * @returns {{void}} No return value; updates internal gesture state and schedules processing after scrolling stops.
   **/
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    // Check if the wheel event is on the calendar container
    if (this.calendarContainer && this.calendarContainer.nativeElement.contains(event.target)) {
      // Prevent default vertical scrolling
      event.preventDefault();

      // Use horizontal scroll delta if available, otherwise use vertical delta
      const deltaX = event.deltaX || event.deltaY;

      if (Math.abs(deltaX) > 0) {
        // Update last wheel event time
        this.lastWheelEventTime = Date.now();

        // Start or continue wheel gesture
        this.isWheelGestureActive = true;

        // Accumulate wheel delta
        this.wheelAccumulator += deltaX;

        // Clear any existing timeout
        if (this.wheelTimeout) {
          clearTimeout(this.wheelTimeout);
        }

        // Set timeout to process gesture after scroll stops
        this.wheelTimeout = setTimeout(() => {
          this.processWheelGestureAfterDelay();
        }, this.wheelTimeoutDelay);
      }
    }
  }

  /**
   * Handle a horizontal swipe gesture on the attendance calendar, detect left/right swipes and navigate dates accordingly.
   * @example
   * handleSwipeGesture()
   * undefined
   * @param {void} - No arguments.
   * @returns {void} No return value.
   */
  private handleSwipeGesture(): void {
    // Only process if gesture hasn't been processed yet
    if (this.gestureProcessed) {
      return;
    }

    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = Math.abs(this.touchEndY - this.touchStartY);

    // Check if it's a horizontal swipe (horizontal distance > vertical distance)
    if (Math.abs(deltaX) > this.scrollThreshold && deltaY < this.maxVerticalDistance) {
      // Mark gesture as processed to prevent multiple date changes
      this.gestureProcessed = true;

      if (this.gestureDirection === 'right') {
        // Swipe right - go to previous date
        this.navigateDate('prev');
      } else if (this.gestureDirection === 'left') {
        // Swipe left - go to next date
        this.navigateDate('next');
      }
    }
  }

  /**
   * Process accumulated wheel gesture after a delay; if the gesture has ended and accumulated delta exceeds the threshold, navigate the calendar date (next or prev) and reset gesture state.
   * @example
   * processWheelGestureAfterDelay()
   * undefined
   * @returns {void} Void when processing is complete.
   **/
  private processWheelGestureAfterDelay(): void {
    // Check if enough time has passed since last wheel event (gesture has ended)
    const timeSinceLastWheel = Date.now() - this.lastWheelEventTime;

    // Only process if gesture has actually ended and we have enough accumulated delta
    if (
      timeSinceLastWheel >= this.wheelTimeoutDelay &&
      this.isWheelGestureActive &&
      Math.abs(this.wheelAccumulator) >= this.wheelThreshold
    ) {
      // Process the date change
      if (this.wheelAccumulator > 0) {
        this.navigateDate('next');
      } else {
        this.navigateDate('prev');
      }
    }

    // Reset all gesture state
    this.wheelAccumulator = 0;
    this.isWheelGestureActive = false;
    this.wheelTimeout = null;
  }

  private setupGlobalWheelListener(): void {
    // Add global wheel listener to detect when scrolling stops
    document.addEventListener('wheel', this.globalWheelHandler);
  }

  private globalWheelHandler = (event: Event): void => {
    const wheelEvent = event as WheelEvent;
    // If the event is not on our calendar, reset our gesture state
    if (
      !this.calendarContainer ||
      !this.calendarContainer.nativeElement.contains(wheelEvent.target)
    ) {
      if (this.isWheelGestureActive) {
        // Process any pending gesture when user scrolls outside calendar
        this.processWheelGestureAfterDelay();
      }
    }
  };

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
